import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { pushAddToCart } from '@theme/data-layer-helper';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;
const ERROR_BUTTON_REENABLE_DELAY = 1000;
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  showLoading() {
    const { addToCartButton } = this.refs;
    addToCartButton.disabled = true;
    addToCartButton.setAttribute('aria-disabled', 'true');
    addToCartButton.classList.add('loading');

    if (!addToCartButton.querySelector('.spinner-icon')) {
      const spinner = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      spinner.classList.add('sprite-icon', 'spinner-icon');
      spinner.setAttribute('width', '18');
      spinner.setAttribute('height', '18');
      spinner.style.position = 'absolute';
      spinner.style.left = '50%';
      spinner.style.top = '50%';
      spinner.style.transform = 'translate(-50%, -50%)';
      
      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#spinner');
      
      spinner.appendChild(use);
      
      const currentPosition = window.getComputedStyle(addToCartButton).position;
      if (currentPosition === 'static') {
        addToCartButton.style.position = 'relative';
      }
      
      addToCartButton.appendChild(spinner);
    }
  }

  hideLoading() {
    const { addToCartButton } = this.refs;
    addToCartButton.classList.remove('loading');
    addToCartButton.removeAttribute('aria-disabled');
    
    const spinner = addToCartButton.querySelector('.spinner-icon');
    if (spinner) {
      spinner.remove();
    }
  }

  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) return;
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';
    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;
    if (!image) return;
    preloadImage(image);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  animateAddToCart() {
    const { addToCartButton } = this.refs;
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLButtonElement | undefined} notifyMeButton - The notify me button for out-of-stock variants.
 * @property {HTMLElement | undefined} variantInfo - The variant-dependent info container (free shipping, final sale).
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    document.addEventListener('color-change', this.#onColorChange, { signal });
    this.#checkForBIS();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      const quantitySelector = this.querySelector('quantity-selector-component');
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      this.#updateQuantityLabel(cartQty);
      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }
async #checkForBIS(newVariantId) {

  const bisEnabled = this.dataset.enableAttentiveBis === 'true';
  const newNotifyMeButton = this.querySelector('#attn-waitlist-trigger');

  if (newNotifyMeButton) {
    this.refs.notifyMeButton = newNotifyMeButton;
    if (newVariantId) {
      newNotifyMeButton.dataset.variantId = String(newVariantId);
    }

    // --- Mutation Observer Logic Start ---
    // Disconnect existing observer if it exists to prevent duplicates
    if (this.notifyObserver) {
      this.notifyObserver.disconnect();
    }

    this.notifyObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'style' || mutation.attributeName === 'class') {
          const isHidden = newNotifyMeButton.classList.contains('hidden');
          const expectedDisplay = isHidden ? 'none' : 'block';
          
          // Only update if the current style differs from our intended state
          if (newNotifyMeButton.style.display !== expectedDisplay) {
            newNotifyMeButton.style.display = expectedDisplay;
          }
        }
      });
    });

    this.notifyObserver.observe(newNotifyMeButton, { 
      attributes: true, 
      attributeFilter: ['style', 'class'] 
    });
    // --- Mutation Observer Logic End ---
  }
  
  const newAddToCartContainer = this.querySelector('add-to-cart-component[ref="addToCartButtonContainer"]');
  const needsSizeSelection = (newAddToCartContainer instanceof HTMLElement) && newAddToCartContainer.dataset.needsSizeSelection === 'true';

  if (newAddToCartContainer) {
    this.refs.addToCartButtonContainer = newAddToCartContainer;
    const isUnavailable = newAddToCartContainer.dataset.soldout === 'true';

    if (isUnavailable && !needsSizeSelection) {
      if (bisEnabled) {
        newNotifyMeButton?.classList.remove('hidden');
        if (newNotifyMeButton) newNotifyMeButton.style.display = 'block';
      } else {
        newAddToCartContainer.classList.remove('hidden');
        newNotifyMeButton?.classList.add('hidden');
        if (newNotifyMeButton) newNotifyMeButton.style.display = 'none';
      }
      newAddToCartContainer.setAttribute('disabled', 'true');
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      newNotifyMeButton?.classList.add('hidden');
      if (newNotifyMeButton) newNotifyMeButton.style.display = 'none';
      newAddToCartContainer.classList.remove('hidden');
      newAddToCartContainer.removeAttribute('disabled');
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }
  }
}

  #onCartUpdate = async (event) => {
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;
    await this.#fetchAndUpdateCartQuantity();
  };

  #onColorChange = (event) => {
    if (!this.isConnected) return;
    
    const { newProductId, newVariantId, variantAvailable } = event.detail;
    const bisEnabled = this.dataset.enableAttentiveBis === 'true';
    
    if (newProductId) {
      this.dataset.productId = newProductId;
    }
    
    // Re-query the variant ID input (it was replaced during DOM swap)
    const newVariantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"][ref="variantId"]')
    );
    if (newVariantInput) {
      /** @type {any} */ (this.refs).variantId = newVariantInput;
      if (newVariantId) {
        newVariantInput.value = String(newVariantId);
      }
    }
    
    // Re-query the notify me button
    const newNotifyMeButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('button[ref="notifyMeButton"]')
    );
    if (newNotifyMeButton) {
      /** @type {any} */ (this.refs).notifyMeButton = newNotifyMeButton;
      if (newVariantId) {
        newNotifyMeButton.dataset.variantId = String(newVariantId);
      }
    }
    
    // Re-query the add to cart button container
    const newAddToCartContainer = /** @type {AddToCartComponent | null} */ (
      this.querySelector('add-to-cart-component[ref="addToCartButtonContainer"]')
    );
    if (newAddToCartContainer) {
      /** @type {any} */ (this.refs).addToCartButtonContainer = newAddToCartContainer;
    }
    
    const newAcceleratedCheckout = this.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (newAcceleratedCheckout) {
      /** @type {any} */ (this.refs).acceleratedCheckoutButtonContainer = newAcceleratedCheckout;
    }
    
    const isUnavailable = newAddToCartContainer.dataset.soldout === 'true';

    if(bisEnabled) {
      newNotifyMeButton.classList.remove('hidden');
    }
    
    if (isUnavailable) {
      if (bisEnabled) {
        newAddToCartContainer?.classList.add('hidden');
        newNotifyMeButton?.classList.remove('hidden');
      } else {
        newAddToCartContainer?.classList.remove('hidden');
        newNotifyMeButton?.classList.add('hidden');
      }
      newAddToCartContainer?.setAttribute('disabled', 'true');
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      newAddToCartContainer.dataset.soldout = 'false';
      newNotifyMeButton?.classList.add('hidden');
      if (newAddToCartContainer) {
        newAddToCartContainer.classList.remove('hidden');
        newAddToCartContainer.removeAttribute('disabled');
      }
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }
    
    this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    event.preventDefault();
    const eventId = `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    if (this.#timeout) clearTimeout(this.#timeout);
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    const quantitySelector = this.querySelector('quantity-selector-component');
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();
        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }
          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });
    addToCartButtonContainer?.showLoading();

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          this.#fetchAndUpdateCartQuantity();
          const variantInput = form?.querySelector('input[name="id"]');
          let metafields = {};
          try {
            if (variantInput?.dataset.productTracking) {
              metafields = JSON.parse(variantInput.dataset.productTracking);
            }
          } catch (e) {
            console.error('Failed to parse product tracking data:', e);
          }

          pushAddToCart(response, metafields, eventId);
          const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: quantity,
              productId: this.dataset.productId,
              sections: response.sections,
              eventId: eventId,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        addToCartButtonContainer?.hideLoading();
        addToCartButtonContainer?.enable();
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;
    const currentNotifyMeButton = this.refs.notifyMeButton;
    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    variantId.value = event.detail.resource?.id ?? '';
    
    if(currentNotifyMeButton) {
      currentNotifyMeButton.classList.add('hidden');
    }

    if (event.detail.resource?.id && variantId && variantId.dataset.productTracking) {
      const variantPicker = this.closest('.shopify-section')?.querySelector('variant-picker');
      const variantsScript = variantPicker?.querySelector('script[data-variants]');
      if (variantsScript?.textContent) {
        try {
          const variantsData = JSON.parse(variantsScript.textContent);
          const selectedVariant = variantsData.variants?.find(
            (v) => String(v.id) === String(event.detail.resource.id)
          );
          
          if (selectedVariant) {
            const trackingData = JSON.parse(variantId.dataset.productTracking);
            trackingData.variant_price = selectedVariant.price || 0;
            trackingData.variant_compare_at_price = selectedVariant.compare_at_price || 0;
            variantId.dataset.productTracking = JSON.stringify(trackingData);
          }
        } catch (e) {
          console.error('Failed to update product tracking data:', e);
        }
      }
    }

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;
    const bisEnabled = this.dataset.enableAttentiveBis === 'true';
    const notifyMeButton = this.refs.notifyMeButton;
    const isUnavailable = event.detail.resource == null || event.detail.resource.available === false;

    if (isUnavailable && notifyMeButton) {
      if (bisEnabled) {
        addToCartButtonContainer?.classList.add('hidden');
        notifyMeButton?.classList.remove('hidden');
      } else {
        addToCartButtonContainer?.classList.remove('hidden');
        notifyMeButton?.classList.add('hidden');
      }
      addToCartButtonContainer?.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      if (event.detail.resource?.id) notifyMeButton.dataset.variantId = String(event.detail.resource.id);
    } else {
      notifyMeButton?.classList.add('hidden');
      if (addToCartButtonContainer) {
        addToCartButtonContainer.classList.remove('hidden');
        addToCartButtonContainer.enable();
        addToCartButtonContainer.removeAttribute('data-needs-size-selection');
      }
      if(isUnavailable) addToCartButtonContainer?.disable();
    }
    // Update button text
    if (currentAddToCartButton && newAddToCartButton) {
      const currentTextSpan = currentAddToCartButton.querySelector('.add-to-cart-text__content');
      const newTextSpan = newAddToCartButton.querySelector('.add-to-cart-text__content');
      if (currentTextSpan && newTextSpan) currentTextSpan.textContent = newTextSpan.textContent;
      const newAriaLabel = newAddToCartButton.getAttribute('aria-label');
      if (newAriaLabel) currentAddToCartButton.setAttribute('aria-label', newAriaLabel);
    }

    // Sync other variant-dependent elements
    const currentVariantInfo = this.refs.variantInfo;
    const newVariantInfo = event.detail.data.html.querySelector('[ref="variantInfo"]');
    if (currentVariantInfo && newVariantInfo) morph(currentVariantInfo, newVariantInfo);

    const currentKlarnaPdp = this.closest('.shopify-section')?.querySelector('[data-klarna-pdp]');
    const newKlarnaPdp = event.detail.data.html.querySelector('[data-klarna-pdp]');
    if (currentKlarnaPdp && newKlarnaPdp) morph(currentKlarnaPdp, newKlarnaPdp);

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (isUnavailable) this.refs.acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
      else this.refs.acceleratedCheckoutButtonContainer.removeAttribute('hidden');
    }

    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      const currentQuantityValue = quantitySelector.getValue?.();
      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {

    this.refs.addToCartButtonContainer?.disable();

  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();
    const offset = { x: rect.width / 2, y: rect.height / 2 };
    const startPoint = { x: sourceRect.left + sourceRect.width / 2 - offset.x, y: sourceRect.top + sourceRect.height / 2 - offset.y };
    const endPoint = { x: destinationRect.left + destinationRect.width / 2 - offset.x, y: destinationRect.top + destinationRect.height / 2 - offset.y };
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 };
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 };

    /** @type {number | null} */
    let startTime = null;
    const duration = 600;
    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      if (progress < 1) requestAnimationFrame(animate);
      else {
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x), bX = 3 * (p2.x - p1.x) - cX, aX = p3.x - p0.x - cX - bX;
  const cY = 3 * (p1.y - p0.y), bY = 3 * (p2.y - p1.y) - cY, aY = p3.y - p0.y - cY - bY;
  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;
  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
